import React, { useState, useEffect } from 'react';
import DataList from '../DataList';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';

const MedicationRequests = ({ admissionId, disableAdd }) => {
  const [medications, setMedications] = useState([]);
  const [planId, setPlanId] = useState(null);

  // Enhanced medications state to include missing items from existing requests
  const [enhancedMedications, setEnhancedMedications] = useState([]);

  const { hasPermission } = useAuth();
  const canForceDispense = hasPermission('/admission-card/medication-requests/force-dispense', 'Execute');

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        // Fetch active treatment plan
        const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/treatment-plans/active`, {
          params: { admission_id: admissionId }
        });

        if (response.data && response.data.plan_items) {
          // Extract medications from plan with type= 'Medication'
          const meds = response.data.plan_items
            .filter(item => item.Type === 'Medication')
            .map(item => ({
              value: item.item_id,
              label: `${item.name} - ${item.Dosage || ''} ${item.route || ''}`
            }));
          console.log('Medications:', meds);
          setMedications(meds);
          setPlanId(response.data.plan_id);
        }
      } catch (error) {
        console.error('Error fetching medications:', error);
      }
    };

    fetchMedications();
  }, [admissionId]);

  // Function to ensure medication options include existing request items
  const ensureMedicationOptionsComplete = (existingRequestedMedication, baseMedications) => {
    if (!existingRequestedMedication || !Array.isArray(existingRequestedMedication)) {
      return baseMedications;
    }

    const enhancedOptions = [...baseMedications];
    
    existingRequestedMedication.forEach(med => {
      if (med.item_id) {
        // Check if this medication is already in the options
        const exists = enhancedOptions.some(opt => opt.value === med.item_id);
        if (!exists) {
          // Add the missing medication option using item_name if available, otherwise use item_id
          const label = med.item_name || `Medication ID: ${med.item_id}`;
          enhancedOptions.push({
            value: med.item_id,
            label: label
          });
          console.log(`Added missing medication option: ${label} (ID: ${med.item_id})`);
        }
      }
    });

    return enhancedOptions;
  };

  // Update enhancedMedications when base medications change
  useEffect(() => {
    setEnhancedMedications(medications);
  }, [medications]);

  // Custom onEdit enhancement handler to enhance medication options for existing records
  // This runs before the FormModal opens, allowing us to enhance dropdown options
  const handleEdit = (record) => {
    // Enhance medication options with any missing items from this record
    const completeMedications = ensureMedicationOptionsComplete(record.requested_medication, medications);
    setEnhancedMedications(completeMedications);
  };

  // create a helper function to validate a security code
  const validateSecurityCode = async (employeeId, securityCode) => {
    // Regular expression to validate numeric characters and length of 4 to 8
    const isValidSecurityCode = /^[a-zA-Z0-9]{4,8}$/.test(securityCode);
    if (!isValidSecurityCode) {
      return false;
    }
    // If valid, call the API to validate the security code
    try {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/verify-code`, {
        employee_id: employeeId,
        security_code: securityCode
      });
      console.log('Security code validation result:', response?.data?.result || 'UNKNOWN');
      return response.data.result === 'Approved';
    } catch (error) {
      console.error('Error validating security code:', error);
      return false;
    }
  };

  const dlConfig = new ApiConfigFactory(
    'admissions/medication-requests', // serverRoute
    'Medication Requests', // title
    'admission_id',
    admissionId
  );

  dlConfig.title = `Medication Requests for Admission: ${admissionId}`;
  dlConfig.allowAdd = planId !== null && !disableAdd;
  dlConfig.allowEdit = true;
  dlConfig.path = '/admission-card/medication-requests';
  dlConfig.defaultSortFieldId = 'request_id';
  
  // Use the proper edit enhancement hook instead of overriding edit behavior
  dlConfig.setEditEnhanceHook(handleEdit);

  // Add bulk action for canceling requests if user has permission
  if (hasPermission('/admission-card/medication-requests/cancel', 'View')) {
    dlConfig.addBulkAction({ 
      name: 'Cancel Request', 
      endpoint: '/hospital-portal/api/admissions/medication-requests/cancel' 
    });
  }

  dlConfig.addDropdownField({ name: 'requested_by', endpoint: '/hospital-portal/api/employees/byrole?role=Nurse', valueField: 'employee_id', labelField: 'employee_name' });
  dlConfig.addDropdownField({ name: 'dispensed_by', endpoint: '/hospital-portal/api/employees/byrole?role=Pharmacy,Management', valueField: 'employee_id', labelField: 'employee_name' });

  dlConfig.additionalValidation = async (formData) => {

    let error = {};

    const dispenserCode = formData.dispenser_code;
    const dispenserId = formData.dispensed_by;
    const requesterCode = formData.requester_code;
    const requesterId = formData.requested_by;
    const hasMedicationChanges = formData.medication_changed;

    const planMismatch = !!(formData?.plan_id && planId && formData.plan_id !== planId);

    // If the active treatment plan changed since the request was created, the form becomes unsavable
    // via the required `valid_request` field. Privileged users may override, but only after an explicit warning.
    // Note: `valid_request` may already be set to 'Yes' to enable the Save button, so the confirm must
    // be driven by the real risk condition (plan mismatch) rather than `valid_request`.
    if (planMismatch && formData.force_dispense_override) {
      console.log('Override flow entered: planMismatch is true and override is checked.');
      if (!canForceDispense) {
        console.warn('Override denied: user lacks permission.');
        error.message = 'You do not have permission to force dispense this request.';
      } else {
        console.warn('Override permitted: showing danger confirmation dialog.');
        const proceed = window.confirm(
          'DANGER: You are about to FORCE DISPENSE a medication request created under a different treatment plan.\n\n' +
            '- This can result in dispensing the wrong medication/dose if the plan changed.\n' +
            '- Use only in emergencies after verifying orders with a doctor and pharmacy SOP.\n\n' +
            'Click OK to proceed, or Cancel to abort.'
        );
        console.log('Override confirmation result:', proceed ? 'CONFIRMED' : 'CANCELLED');
        if (!proceed) {
          error.message = 'Force dispense cancelled. Review the active treatment plan and create a new request if needed.';
        } else {
          // Bypass the required-field block for this submission only.
          formData.valid_request = 'Yes';
          console.log('Override applied: valid_request set to Yes for this submission.');
        }
      }
    }

    const isTakeHome = formData.request_type === 'discharge';

    // if dispensed is true, then dispensed_by and dispenser_code are required
    if (formData.dispensed) {
      console.log('Dispensed validation: running required fields checks.');
      if (!dispenserId) {
        console.warn('Dispensed validation failed: missing dispensed_by.');
        error.message = 'Dispenser is required';
      } else if (!dispenserCode) {
        console.warn('Dispensed validation failed: missing dispenser_code.');
        error.message = 'Dispenser Code is required';
      }
      if (!isTakeHome && !requesterId) {
        console.warn('Dispensed validation failed: missing requested_by.');
        error.message = 'Requester is required';
      }
      // Check that requested_medication is not empty
      if (!formData.requested_medication || formData.requested_medication.length === 0) {
        console.warn('Dispensed validation failed: requested_medication empty.');
        error.message = 'Requested Medication is required';
      }
    }
    // Validate dispenser code
    if (dispenserCode) {
      console.log('Validating dispenser code (value not logged).');
      const isValidDispenser = await validateSecurityCode(dispenserId, dispenserCode);
      console.log('Dispenser code validation result:', isValidDispenser ? 'APPROVED' : 'REJECTED');
      if (!isValidDispenser) {
        error.message = 'Invalid Dispenser Code';
      }
    }
    if (hasMedicationChanges) {
      console.log('Medication changed validation: running requester checks.');
      if (!requesterId) {
        console.warn('Medication changed validation failed: missing requested_by.');
        error.message = 'Requester is required';
      } else if (!requesterCode) {
        console.warn('Medication changed validation failed: missing requester_code.');
        error.message = 'Requester Code is required';
      }
    }
    // Validate requester code
    if (requesterCode) {
      console.log('Validating requester code (value not logged).');
      const isValidRequester = await validateSecurityCode(requesterId, requesterCode);
      console.log('Requester code validation result:', isValidRequester ? 'APPROVED' : 'REJECTED');
      if (!isValidRequester) {
        error.message = 'Invalid Requester Code';
      }
    }

    if (error.message) {
      error.isValid = false;
    } else {
      error.isValid = true;
    }
    return error;
  };

  dlConfig
    // Add hidden field for tracking medication changes
    .addField({
      name: 'medication_changed',
      label: 'Medication Changed',
      type: 'checkbox',
      defaultValue: false,
      visible: false
    })
    .addField({
      name: 'force_dispense_override',
      label: 'Force Dispense Override (Emergency Only)',
      type: 'checkbox',
      defaultValue: false,
      visible: canForceDispense,
      tab: 'Pharmacy',
      onInitialize: (value, formData) => {
        const hasPlanMismatch = formData.plan_id && planId && formData.plan_id !== planId;
        // Only show when mismatch exists AND user has permission.
        // This mutates the field config for the current modal render.
        // (FormModal uses `field.visible === false` to hide).
        // eslint-disable-next-line no-param-reassign
        dlConfig.fields.find(f => f.name === 'force_dispense_override').visible = !!(hasPlanMismatch && canForceDispense);

        return hasPlanMismatch ? !!value : false;
      },
      onChange: (value, formData, setFormData) => {
        const hasPlanMismatch = formData.plan_id && planId && formData.plan_id !== planId;
        if (!hasPlanMismatch) {
          setFormData({ ...formData, force_dispense_override: false });
          return;
        }
        const next = { ...formData, force_dispense_override: value };
        // Enable/disable save immediately by syncing the required field.
        if (value && canForceDispense) {
          next.valid_request = 'Yes';
        }
        setFormData(next);
      }
    })
    .addField({
      name: 'valid_request',
      label: 'Valid Request',
      type: 'text',
      defaultValue: 'Yes',
      visible: false,
      required: true,
      onInitialize: (value, formData) => {
        //console.log('onInitialize', value, formData.plan_id," Plan ID: ", planId);
        if (formData.force_dispense_override && canForceDispense) {
          return 'Yes';
        }
        if (formData.plan_id && formData.plan_id !== planId) {
          return '';
        } else {
          return 'Yes';
        }
      },
      tab: 'Request'

    })
    // Add a status field and disable it
    .addField({ name: 'request_type', label: 'Request Type', type: 'text', disabled: true, tab: 'Request', defaultValue: 'inpatient' })
    .addField({ name: 'status', label: 'Status', type: 'text', disabled: true, tab: 'Request' })
    .addField({ name: 'request_id', label: 'Request ID', type: 'number', identifier: true, disabled: true, tab: 'Request' })
    .addField({ name: 'plan_id', label: 'Plan ID', type: 'number', defaultValue: planId, visible: true, tab: 'Request', disabled: true })
    .addField({ name: 'admission_id', label: 'Admission ID', type: 'number', defaultValue: admissionId, visible: false, tab: 'Request' })
    .addField({ name: 'requested_by', label: 'Requested By', type: 'dropdown', required: false, tab: 'Request' })
    .addField({ name: 'requester_code', label: 'Requester Code', type: 'code', length: 4, tab: 'Request' })
    .addField({
      name: 'request_date',
      label: 'Request Date',
      type: 'datetime-local',
      required: true,
      defaultValue: new Date().toISOString().slice(0, 16),
      tab: 'Request'
    })
    .addField({
      name: 'requested_medication',
      label: 'Medications',
      type: 'subform',
      subFields: [
        {
          name: 'item_id',
          label: 'Medication',
          type: 'dropdown',
          width: '200px',
          required: true,
          options: enhancedMedications
        },
        { name: 'quantity', label: 'Qty', type: 'number', width: '40px' },
      ],
      required: true,
      tab: 'Request',
      onChange: (value, formData, setFormData) => {
        if (value && value.length > 0) {
          setFormData({ ...formData, medication_changed: true });
        }
      }
    })
    .addField({ name: 'notes', label: 'Special Note', type: 'textarea', maxRows: 3, tab: 'Request' })
    .addField({ name: 'password', label: 'Password', type: 'password', required: false, tab: 'Pharmacy', visible: false })
    .addField({ 
      name: 'dispensed_by', 
      label: 'Dispensed By', 
      type: 'dropdown', 
      tab: 'Pharmacy',
      onChange: (value, formData, setFormData) => {
        // Prevent editing if record is new (no request_id)
        if (!formData.request_id) {
          alert('Pharmacy fields cannot be edited until the medication request is saved first.');
          setFormData({ ...formData, dispensed_by: null }); // Reset to empty
          return;
        }
        // If dispensed by is changed, set dispensed to true
        if (value) {
          setFormData({ ...formData, dispensed_by: value, dispensed: true });
        } else {
          setFormData({ ...formData, dispensed_by: value });
        }
      } 
    })
    .addField({ 
      name: 'dispenser_code', 
      label: 'Dispenser Code', 
      type: 'code', 
      length: 4, 
      tab: 'Pharmacy',
      onChange: (value, formData, setFormData) => {
        // Prevent editing if record is new (no request_id)
        if (!formData.request_id) {
          alert('Pharmacy fields cannot be edited until the medication request is saved first.');
          setFormData({ ...formData, dispenser_code: '' }); // Reset to empty
          return;
        }
        setFormData({ ...formData, dispenser_code: value });
      }
    })
    .addField({
      name: 'dispensed_date',
      label: 'Dispensed Date',
      type: 'datetime-local',
      defaultValue: new Date().toISOString().slice(0, 16),
      tab: 'Pharmacy',
      onChange: (value, formData, setFormData) => {
        // Prevent editing if record is new (no request_id)
        if (!formData.request_id) {
          alert('Pharmacy fields cannot be edited until the medication request is saved first.');
          setFormData({ ...formData, dispensed_date: '' }); // Reset to empty
          return;
        }
        setFormData({ ...formData, dispensed_date: value });
      }
    })
    .addField({
      name: 'dispensed',
      label: 'Dispensed',
      type: 'checkbox',
      defaultValue: false,
      visible: false,
      tab: 'Pharmacy',
    })
    .addField({ name: 'dispensed', label: 'Dispense and Bill', type: 'checkbox', defaultValue: false, tab: 'Admin' })
    .addField({ name: 'updatedBy', label: 'Updated By', type: 'text', required: false, tab: 'Admin' })
    .addField({ name: 'updatedAt', label: 'Updated At', type: 'datetime', required: false, tab: 'Admin', disabled: true })
    .addField({ name: 'createdAt', label: 'Created At', type: 'datetime', required: false, tab: 'Admin', disabled: true })
    .addField({ name: 'patient_billing_id', label: 'Bill ID', type: 'text', required: false, tab: 'Admin' });

  dlConfig
    .addTab({ name: 'Request', roles: 'all' })
    .addTab({ name: 'Admin', roles: 'Admin' })
    .addTab({ name: 'Pharmacy', roles: 'all' });

  dlConfig
    .addColumn({
      id: 'request_type',
      name: 'Type',
      selector: (row) => row.request_type === 'discharge' ? 'Take-home' : 'Inpatient',
      cell: (row) => row.request_type === 'discharge'
        ? <span style={{ background: '#e8f5e9', color: '#1b5e20', fontWeight: 700, padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem' }}>Take-home</span>
        : 'Inpatient',
      type: 'text',
      sortable: true,
      width: '110px',
    })
    .addColumn({ id: 'status', name: 'Status', selector: (row) => row.status, type: 'text', sortable: true })
    .addColumn({
      id: 'request_id',
      name: 'Request ID',
      selector: (row) => row.request_id,
      type: 'number',
      sortable: true,
      identifier: true,
      width: '100px',
      filterable: true,
      filterSelector: 'request_id'
    })
    .addColumn({ id: 'plan_id', name: 'Plan ID', selector: (row) => row.plan_id, type: 'number', sortable: true })
    .addColumn({ id: 'requested_by_nurse', name: 'Requested By', selector: (row) => row.requester?.employee_name || (row.request_type === 'discharge' ? 'Doctor (discharge)' : ''), type: 'text', sortable: true })
    .addColumn({ id: 'request_date', name: 'Request Date', selector: (row) => row.request_date, type: 'datetime', sortable: true });

  dlConfig.conditionalRowStyles = [
    {
      when: (row) => row.request_type === 'discharge',
      style: {
        backgroundColor: '#e8f5e9',
        borderLeft: '3px solid #2e7d32',
      },
    },
  ];

  return <DataList apiConfig={dlConfig} columns={dlConfig.columns} />;
};

export default MedicationRequests;
